import {
  Button,
  Col,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Layout,
  message,
  Row,
  Select,
} from "antd";
import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import Navbar from "../../Components/Navbar/Navbar";
import PageHeader from "../../Components/PageHeader/PageHeader";
import ReasonModal from "../../Components/CustomModals/ReasonModal";
import { formatString, navigateTo } from "../../Utils/Common";
import { getRequest, postRequest } from "../../Utils/ApiCalls";
import dayjs from "dayjs";
import PersistentFooter from "../../Components/PersistentFooter/PersistentFooter";
import { useParams } from "react-router";

interface DailySalesReportFormProps {
  add?: boolean;
  edit?: boolean;
  review?: boolean;
}

const { Option } = Select;

const PAYMENT_METHODS = ["Cash", "GCash", "Credit Card", "Bank Transfer", "Check"];
const STATUSES        = ["pending", "completed", "cancelled"];
const TEST_TYPES      = ["Microbiological", "Physical-Chemical", "Heavy Metals", "Package Test"];

const DailySalesReportForm: React.FC<DailySalesReportFormProps> = ({
  add,
  edit,
  review,
}) => {
  const [loading,        setLoading]       = useState("");
  const [showReasonModal, setShowReasonModal] = useState(false);
  const [cancelReason,   setCancelReason]  = useState("");
  const [customers,      setCustomers]     = useState<any[]>([]);
  const [form]                             = Form.useForm();

  const { id: transaction_id } = useParams<{ id: string }>();

  const isReadOnly = review;

  function clearFields() {
    form.resetFields();
  }

  async function submitForm(data: any) {
    const formattedData: any = {
      ...data,
      transaction_date: data.transaction_date
        ? dayjs(data.transaction_date).format("YYYY-MM-DD HH:mm:ss")
        : "",
      date_sampling: data.date_sampling
        ? dayjs(data.date_sampling).format("YYYY-MM-DD")
        : "",
      paid_date: data.paid_date
        ? dayjs(data.paid_date).format("YYYY-MM-DD HH:mm:ss")
        : "",
    };

    if (edit) formattedData.transaction_id = transaction_id;

    setLoading("submit");
    try {
      const response = await postRequest(
        add ? "daily_transactions/create" : "daily_transactions/update",
        formattedData,
        true
      );

      if (response.success) {
        message.success(formatString(response.data.message));
        setTimeout(() => {
          clearFields();
          navigateTo("/daily-sales-report");
        }, 1500);
      } else {
        message.error(
          response?.data?.response?.data?.messages?.error_messages?.[0] ??
            `Failed to ${add ? "create" : "update"} daily sales report.`
        );
      }
    } catch {
      message.error("Something went wrong. Please try again.");
    } finally {
      setLoading("");
    }
  }

  async function cancelTransaction() {
    if (!cancelReason.trim()) return;
    setShowReasonModal(false);
    setLoading("cancel");
    try {
      const response = await postRequest(
        "daily_transactions/change_status",
        { transaction_id, action: "cancel", reason: cancelReason },
        true
      );
      if (response.success) {
        message.success(formatString(response.data.message));
        setTimeout(() => {
          clearFields();
          navigateTo("/daily-sales-report");
        }, 1500);
      } else {
        message.error(
          response?.data?.response?.data?.messages?.error_messages?.[0] ??
            "Failed to cancel transaction."
        );
      }
    } catch {
      message.error("Something went wrong. Please try again.");
    } finally {
      setLoading("");
    }
  }

  async function fetchCustomers() {
    setCustomers([]);
    const response = await getRequest("companies/get", {}, true);
    if (response.success) setCustomers(response.data.data);
  }

  async function fetchTransactionData() {
    if (!transaction_id) return;
    setLoading("fetch");
    const response = await getRequest("daily_transactions/get", { transaction_id }, true);
    if (response.success) {
      const data = response.data.data[0];
      form.setFieldsValue({
        rwt_no:               data.rwt_no,
        payment_method:       data.payment_method,
        transaction_date:     data.transaction_date ? dayjs(data.transaction_date) : null,
        customer_id:          data.customer_id ? Number(data.customer_id) : null,
        sample_count:         data.sample_count,
        date_sampling:        data.date_sampling ? dayjs(data.date_sampling) : null,
        package_test:         data.package_test,
        test_type:            data.test_type,
        special_instruction:  data.special_instruction,
        sample_received_by:   data.sample_received_by,
        processed_by:         data.processed_by,
        lab_received_by:      data.lab_received_by,
        lab_analyzed_by:      data.lab_analyzed_by,
        lab_analyzed_by_2:    data.lab_analyzed_by_2,
        lab_remark:           data.lab_remark,
        lab_reference:        data.lab_reference,
        head_chemist_approved_by: data.head_chemist_approved_by,
        printed_by:           data.printed_by,
        total_amount:         data.total_amount,
        total_tested_amount:  data.total_tested_amount,
        remaining_balance:    data.remaining_balance,
        payment_remarks:      data.payment_remarks,
        paid_date:            data.paid_date ? dayjs(data.paid_date) : null,
        is_paid:              data.is_paid,
        ar_no:                data.ar_no,
        status:               data.status,
      });
    }
    setLoading("");
  }

  useEffect(() => {
    fetchCustomers();
    if (edit || review) fetchTransactionData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const pageTitle = add    ? "Add Daily Sales Report"
    : edit   ? "Edit Daily Sales Report"
    : review ? "Review Daily Sales Report"
    :          "View Daily Sales Report";

  const SectionLabel = ({ label }: { label: string }) => (
    <Col xs={24}>
      <div className="text-sm font-semibold text-primary border-b pb-1 mb-1 mt-3">
        {label}
      </div>
    </Col>
  );

  return (
    <Layout className="page">
      <Helmet><title>Agua Lab ERP System</title></Helmet>
      <Navbar />
      <Layout>
        <Row className="px-3 pt-20 md:px-10 md:pb-10 md:pt-8 w-screen md:w-full pb-[50px] sm:pb-0 overflow-scroll text-textColor">
          <Col xs={24}>
            <PageHeader
              title={pageTitle}
              withDateRangeFilter={false}
              withAddButton={false}
              withSearchBtn={false}
            />
          </Col>

          {loading !== "fetch" && (
            <div className="form-container w-full">
              <Col span={24}>
                <Form form={form} onFinish={submitForm} layout="vertical">
                  <Row className="w-full" gutter={[10, 10]}>

                    {/* Transaction Info */}
                    <SectionLabel label="Transaction Information" />

                    <Col xs={24} lg={8}>
                      <Form.Item
                        label="RWT Number"
                        name="rwt_no"
                        rules={[{ required: true, message: "This field is required." }]}
                      >
                        <Input disabled={isReadOnly} placeholder="Enter RWT number" />
                      </Form.Item>
                    </Col>

                    <Col xs={24} lg={8}>
                      <Form.Item
                        label="Transaction Date & Time"
                        name="transaction_date"
                        rules={[{ required: true, message: "This field is required." }]}
                      >
                        <DatePicker
                          style={{ width: "100%" }}
                          showTime
                          format="YYYY-MM-DD HH:mm"
                          placeholder="Select date and time"
                          disabled={isReadOnly}
                        />
                      </Form.Item>
                    </Col>

                    <Col xs={24} lg={8}>
                      <Form.Item
                        label="AR Number"
                        name="ar_no"
                      >
                        <Input disabled={isReadOnly} placeholder="Enter AR number" />
                      </Form.Item>
                    </Col>

                    <Col xs={24} lg={12}>
                      <Form.Item
                        label="Customer / Client"
                        name="customer_id"
                        rules={[{ required: true, message: "This field is required." }]}
                      >
                        <Select
                          placeholder="Select customer"
                          showSearch
                          allowClear
                          optionFilterProp="label"
                          disabled={isReadOnly}
                          options={customers.map((c: any) => ({
                            label: c.name,
                            value: Number(c.id),
                          }))}
                        />
                      </Form.Item>
                    </Col>

                    <Col xs={24} lg={12}>
                      <Form.Item
                        label="Status"
                        name="status"
                      >
                        <Select
                          placeholder="Select status"
                          disabled={isReadOnly}
                          allowClear
                        >
                          {STATUSES.map((s) => (
                            <Option key={s} value={s}>
                              {s.charAt(0).toUpperCase() + s.slice(1)}
                            </Option>
                          ))}
                        </Select>
                      </Form.Item>
                    </Col>

                    {/* Sample Info */}
                    <SectionLabel label="Sample Information" />

                    <Col xs={24} lg={8}>
                      <Form.Item
                        label="Test Type"
                        name="test_type"
                        rules={[{ required: true, message: "This field is required." }]}
                      >
                        <Select
                          placeholder="Select test type"
                          disabled={isReadOnly}
                          allowClear
                        >
                          {TEST_TYPES.map((t) => (
                            <Option key={t} value={t}>{t}</Option>
                          ))}
                        </Select>
                      </Form.Item>
                    </Col>

                    <Col xs={24} lg={8}>
                      <Form.Item label="Package Test" name="package_test">
                        <Input disabled={isReadOnly} placeholder="Enter package test" />
                      </Form.Item>
                    </Col>

                    <Col xs={24} lg={8}>
                      <Form.Item
                        label="Sample Count"
                        name="sample_count"
                        rules={[{ required: true, message: "This field is required." }]}
                      >
                        <InputNumber
                          style={{ width: "100%" }}
                          min={0}
                          disabled={isReadOnly}
                          placeholder="0"
                        />
                      </Form.Item>
                    </Col>

                    <Col xs={24} lg={12}>
                      <Form.Item label="Date of Sampling" name="date_sampling">
                        <DatePicker
                          style={{ width: "100%" }}
                          format="YYYY-MM-DD"
                          placeholder="Select date"
                          disabled={isReadOnly}
                        />
                      </Form.Item>
                    </Col>

                    <Col xs={24} lg={12}>
                      <Form.Item label="Special Instructions" name="special_instruction">
                        <Input.TextArea
                          rows={2}
                          disabled={isReadOnly}
                          placeholder="Enter any special instructions"
                        />
                      </Form.Item>
                    </Col>

                    {/* Lab Personnel */}
                    <SectionLabel label="Lab Personnel" />

                    <Col xs={24} lg={8}>
                      <Form.Item label="Sample Received By" name="sample_received_by">
                        <Input disabled={isReadOnly} placeholder="Name" />
                      </Form.Item>
                    </Col>

                    <Col xs={24} lg={8}>
                      <Form.Item label="Processed By" name="processed_by">
                        <Input disabled={isReadOnly} placeholder="Name" />
                      </Form.Item>
                    </Col>

                    <Col xs={24} lg={8}>
                      <Form.Item label="Lab Received By" name="lab_received_by">
                        <Input disabled={isReadOnly} placeholder="Name" />
                      </Form.Item>
                    </Col>

                    <Col xs={24} lg={8}>
                      <Form.Item label="Lab Analyzed By" name="lab_analyzed_by">
                        <Input disabled={isReadOnly} placeholder="Name" />
                      </Form.Item>
                    </Col>

                    <Col xs={24} lg={8}>
                      <Form.Item label="Lab Analyzed By 2" name="lab_analyzed_by_2">
                        <Input disabled={isReadOnly} placeholder="Name" />
                      </Form.Item>
                    </Col>

                    <Col xs={24} lg={8}>
                      <Form.Item label="Head Chemist Approved By" name="head_chemist_approved_by">
                        <Input disabled={isReadOnly} placeholder="Name" />
                      </Form.Item>
                    </Col>

                    <Col xs={24} lg={8}>
                      <Form.Item label="Printed By" name="printed_by">
                        <Input disabled={isReadOnly} placeholder="Name" />
                      </Form.Item>
                    </Col>

                    <Col xs={24} lg={8}>
                      <Form.Item label="Lab Reference" name="lab_reference">
                        <Input disabled={isReadOnly} placeholder="Enter lab reference" />
                      </Form.Item>
                    </Col>

                    <Col xs={24} lg={8}>
                      <Form.Item label="Lab Remarks" name="lab_remark">
                        <Input disabled={isReadOnly} placeholder="Enter remarks" />
                      </Form.Item>
                    </Col>

                    {/* Payment Info */}
                    <SectionLabel label="Payment Information" />

                    <Col xs={24} lg={8}>
                      <Form.Item
                        label="Payment Method"
                        name="payment_method"
                        rules={[{ required: true, message: "This field is required." }]}
                      >
                        <Select
                          placeholder="Select payment method"
                          disabled={isReadOnly}
                          allowClear
                        >
                          {PAYMENT_METHODS.map((m) => (
                            <Option key={m} value={m}>{m}</Option>
                          ))}
                        </Select>
                      </Form.Item>
                    </Col>

                    <Col xs={24} lg={8}>
                      <Form.Item
                        label="Total Amount"
                        name="total_amount"
                        rules={[{ required: true, message: "This field is required." }]}
                      >
                        <InputNumber
                          style={{ width: "100%" }}
                          min={0}
                          prefix="₱"
                          disabled={isReadOnly}
                          placeholder="0.00"
                        />
                      </Form.Item>
                    </Col>

                    <Col xs={24} lg={8}>
                      <Form.Item label="Total Tested Amount" name="total_tested_amount">
                        <InputNumber
                          style={{ width: "100%" }}
                          min={0}
                          prefix="₱"
                          disabled={isReadOnly}
                          placeholder="0.00"
                        />
                      </Form.Item>
                    </Col>

                    <Col xs={24} lg={8}>
                      <Form.Item label="Remaining Balance" name="remaining_balance">
                        <InputNumber
                          style={{ width: "100%" }}
                          min={0}
                          prefix="₱"
                          disabled={isReadOnly}
                          placeholder="0.00"
                        />
                      </Form.Item>
                    </Col>

                    <Col xs={24} lg={8}>
                      <Form.Item label="Is Paid" name="is_paid">
                        <Select
                          placeholder="Select"
                          disabled={isReadOnly}
                          allowClear
                        >
                          <Option value="1">Yes</Option>
                          <Option value="0">No</Option>
                        </Select>
                      </Form.Item>
                    </Col>

                    <Col xs={24} lg={8}>
                      <Form.Item label="Paid Date & Time" name="paid_date">
                        <DatePicker
                          style={{ width: "100%" }}
                          showTime
                          format="YYYY-MM-DD HH:mm"
                          placeholder="Select date and time"
                          disabled={isReadOnly}
                        />
                      </Form.Item>
                    </Col>

                    <Col xs={24} lg={24}>
                      <Form.Item label="Payment Remarks" name="payment_remarks">
                        <Input.TextArea
                          rows={2}
                          disabled={isReadOnly}
                          placeholder="Enter payment remarks"
                        />
                      </Form.Item>
                    </Col>

                    {/* Action Buttons */}
                    <Col lg={24} className="mt-5 flex justify-end gap-3">
                      <Button
                        onClick={() => {
                          navigateTo("/daily-sales-report");
                          clearFields();
                        }}
                      >
                        {isReadOnly ? "Close" : "Cancel"}
                      </Button>

                      {review && (
                        <Button
                          danger
                          loading={loading === "cancel"}
                          onClick={() => {
                            setCancelReason("");
                            setShowReasonModal(true);
                          }}
                        >
                          Cancel Transaction
                        </Button>
                      )}

                      {(add || edit) && (
                        <Button
                          htmlType="submit"
                          type="primary"
                          loading={loading === "submit"}
                        >
                          {add ? "Submit" : "Update"}
                        </Button>
                      )}
                    </Col>

                  </Row>
                </Form>
              </Col>
            </div>
          )}
        </Row>
      </Layout>

      <ReasonModal
        showModal={showReasonModal}
        onCancel={() => setShowReasonModal(false)}
        title="Please provide a reason for cancellation:"
        reasonValue={cancelReason}
        onReasonChange={(e: any) => setCancelReason(e.target.value)}
        footer={[
          <Button key="cancel" onClick={() => setShowReasonModal(false)}>
            Cancel
          </Button>,
          <Button
            key="confirm"
            type="primary"
            danger
            loading={loading === "cancel"}
            disabled={!cancelReason.trim()}
            onClick={cancelTransaction}
          >
            Confirm
          </Button>,
        ]}
      />

      <PersistentFooter />
    </Layout>
  );
};

export default DailySalesReportForm;